// Lion Auto templates (LaTemplatesModal): a show's groups saved once and
// applied to the artist's other dates. Save copies the ticked groups of the
// show on screen into a template and links them to it; Update from this show
// re-saves it (version + 1). Apply runs on the shows the pricer ticks, ONE
// AT A TIME (runTemplateApply): on each show the template creates the groups
// it is missing (Auto off), updates the linked ones' filter fields (sections,
// rows, Qty, splits, notes filter; their floor, ladder and hold stay) and
// takes the listings a template group fits from the show's own hand-made
// groups, ranked ones too. Each section is matched by name on that show.
// A group made by Lion Checker or linked to another template can't be saved
// into one. Each apply sends the version the pricer saw in the list; the
// server answers 409 TEMPLATE_STALE when the template was re-saved since,
// which stops the run until the pricer reloads. A 429 or a 404 (the template
// deleted) stops it too. A show whose apply skipped groups, missed sections
// or left listings where they were is "partial", not done. A write with no
// answer, a 502 / 504, or a 5xx without the server's JSON { error } has an
// unknown outcome (it may have gone through): warn style, reload to check.
// Pure; no api import.
import { detailLines, retryAfterSec } from './lionAutoErrors';
import { agoText } from './lionAutoRunner';
import { parseUtcDate } from './dates';

// A template name's limit; mirrors the server's TEMPLATE_LIMITS.name
// (backend/services/lionAuto/templateRules.js): change both together.
export const TEMPLATE_NAME_MAX = 60;

const asArray = (x) => (Array.isArray(x) ? x : []);
const asSet = (x) => (x instanceof Set ? x : new Set());
const isObject = (x) => !!x && typeof x === 'object';
const isGroup = (g) => isObject(g) && g.id != null;
const isShow = (s) => isObject(s) && s.eventId != null;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const trimmed = (s) => (typeof s === 'string' ? s.trim() : '');

// A count from the server: a whole number >= 0 (strings too), else 0
const count = (v) => {
  if ((typeof v !== 'number' && typeof v !== 'string') || String(v).trim() === '') return 0;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : 0;
};
// A template version: a whole number >= 1, else null (unknown)
const versionOf = (v) => {
  if ((typeof v !== 'number' && typeof v !== 'string') || String(v).trim() === '') return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 ? n : null;
};

// "a, b, c +4 more": the first max labels, then how many are left out
function cutList(labels, max) {
  if (labels.length <= max) return labels.join(', ');
  return `${labels.slice(0, max).join(', ')} +${labels.length - max} more`;
}

// ---- Save / Update from this show ----

/**
 * This show's groups as save-form rows: { group, reason, linked }. reason
 * (why it can't be ticked) is "from Lion Checker" for a group a Lion Checker
 * import made, "in another template" for one linked to a template other than
 * templateId; null when it can be saved. linked: it is linked to templateId
 * (Update from this show of that template). Ids compare as strings.
 * @param {object[]} groups  the show's GroupViews
 * @param {number|string|null} [templateId]  the template being updated; null for a new one
 * @returns {{ group: object, reason: string|null, linked: boolean }[]}
 */
export function saveableGroups(groups, templateId = null) {
  const tid = templateId == null ? null : String(templateId);
  return asArray(groups).filter(isGroup).map((g) => {
    const inTemplate = g.templateId != null;
    const linked = tid != null && inTemplate && String(g.templateId) === tid;
    let reason = null;
    if (g.checkerEventId != null || g.checkerFilterIndex != null) reason = 'from Lion Checker';
    else if (inTemplate && !linked) reason = 'in another template';
    return { group: g, reason, linked };
  });
}

/**
 * The save form's first ticks (group id strings): an update ticks the
 * groups already linked to its template, when this show has any; otherwise
 * every saveable group is ticked.
 * @param {{ group, reason, linked }[]} rows  saveableGroups
 * @returns {Set<string>}
 */
export function defaultSaveSelection(rows) {
  const ok = asArray(rows).filter(r => isObject(r) && !r.reason && isGroup(r.group));
  const linked = ok.filter(r => r.linked);
  return new Set((linked.length ? linked : ok).map(r => String(r.group.id)));
}

/**
 * The body's groupIds: the ticked saveable groups' ids, in the list's order
 * (a disabled row or a tick no row has is dropped).
 * @param {Set<string>} selected  ticked group id strings
 * @param {{ group, reason }[]} rows  saveableGroups
 */
export function saveGroupIds(selected, rows) {
  const ticked = asSet(selected);
  return asArray(rows)
    .filter(r => isObject(r) && !r.reason && isGroup(r.group) && ticked.has(String(r.group.id)))
    .map(r => r.group.id);
}

/** Every key is ticked, and there is at least one (the tick-all box) */
export function everyTicked(selected, keys) {
  const ticked = asSet(selected);
  const list = asArray(keys);
  return list.length > 0 && list.every(k => ticked.has(k));
}

/** Why a template name can't be saved, or null: required, at most 60 characters (trimmed) */
export function templateNameError(name) {
  const s = trimmed(name);
  if (!s) return 'Give the template a name';
  if (s.length > TEMPLATE_NAME_MAX) return `At most ${TEMPLATE_NAME_MAX} characters`;
  return null;
}

/** The name a new template starts with: "performer · venue", cut to the limit; "Template" for neither */
export function defaultTemplateName(performer, venue) {
  const name = [trimmed(performer), trimmed(venue)].filter(Boolean).join(' · ');
  if (!name) return 'Template';
  return name.slice(0, TEMPLATE_NAME_MAX).trimEnd();
}

// ---- Apply to shows ----

/** A show's tick key: its event id as a string */
export function showKey(show) {
  return String(show?.eventId);
}

const STATUS = {
  source: { key: 'source', text: 'Saved from here', kind: 'neutral' },
  current: { key: 'current', text: 'Up to date', kind: 'ok' },
  older: { key: 'older', text: 'Older version', kind: 'warn' },
  none: { key: 'none', text: 'Not applied', kind: 'neutral' },
  // linked, but the template's version is not known: neither current nor older
  applied: { key: 'applied', text: 'Applied', kind: 'neutral' },
};

/**
 * One show against the template's version: "Saved from here" (the show it
 * was saved from), "Up to date" (its linked groups carry this version),
 * "Older version" (an older one, or none stamped), "Not applied" (no linked
 * group). With an unknown version a linked show reads "Applied".
 * @param {object} show  a GET shows entry
 * @param {*} version  the template's version (the shows answer's)
 * @returns {{ key: string, text: string, kind: 'ok'|'warn'|'neutral' }}
 */
export function showStatus(show, version) {
  if (!isObject(show)) return { key: 'none', text: '', kind: 'neutral' };
  if (show.isSource === true) return { ...STATUS.source };
  if (!count(show.linked)) return { ...STATUS.none };
  const current = versionOf(version);
  if (current === null) return { ...STATUS.applied };
  const linkedVersion = versionOf(show.linkedVersion);
  return { ...(linkedVersion !== null && linkedVersion >= current ? STATUS.current : STATUS.older) };
}

/**
 * The shows ticked when the Apply list opens: same venue, not the show it
 * was saved from, and not up to date (not applied, or an older version).
 * @param {object[]} shows
 * @param {*} version  the template's version
 * @returns {Set<string>} show keys
 */
export function defaultShowSelection(shows, version) {
  return new Set(asArray(shows)
    .filter(s => isShow(s) && s.sameVenue === true && !s.isSource
      && ['none', 'older'].includes(showStatus(s, version).key))
    .map(showKey));
}

/** The ticked shows, in the server's (date) order; a tick no show has is dropped */
export function tickedShows(selected, shows) {
  const ticked = asSet(selected);
  return asArray(shows).filter(s => isShow(s) && ticked.has(showKey(s)));
}

/** "2 other groups (1 Auto)": the show's groups not linked to this template; '' for none */
export function otherGroupsText(show) {
  const n = count(show?.otherGroups);
  if (!n) return '';
  const auto = Math.min(count(show.otherAuto), n);
  return `${plural(n, 'other group')}${auto ? ` (${auto} Auto)` : ''}`;
}

const CACHE_PARTIAL_NOTE = 'Skybox inventory is only partly loaded: some shows may be missing.';

/** The shows answer's note when Skybox inventory is only partly loaded (cachePartial), else '' */
export function showsCacheNote(data) {
  return data?.cachePartial === true ? CACHE_PARTIAL_NOTE : '';
}

/**
 * The version an apply sends: the one the pricer saw in the list (the
 * TemplateView's), a whole number of at least 1; null when it is not one
 * (Apply refuses to run).
 * @param {object|null} template  the TemplateView
 */
export function applyVersionOf(template) {
  return versionOf(template?.version);
}

/**
 * The shows answer was read at another version than the list showed: the
 * template was re-saved since (Reload). False when either is unknown.
 * @param {object|null} template  the TemplateView
 * @param {object|null} showsData  the GET shows answer
 */
export function showsVersionChanged(template, showsData) {
  const listed = versionOf(template?.version);
  const read = versionOf(showsData?.version);
  return listed !== null && read !== null && listed !== read;
}

/** The stale banner: "This template was re-saved since this list loaded (now version 4). Reload, …" */
export function templateStaleText(version) {
  const v = versionOf(version);
  return `This template was re-saved since this list loaded${v ? ` (now version ${v})` : ''}. `
    + 'Reload, check the shows, then apply again.';
}

/**
 * Why Apply can't run now (its button's title), or null when it can: a run
 * in flight, the template gone, an unknown version, re-saved since the list
 * loaded (a 409 stopped the last run, or the shows answer carries another
 * version), the shows still reading, or nothing ticked.
 * @param {{ template, showsData, isFetching, running, stale, tickedCount }} state
 */
export function applyBlockedReason({ template, showsData, isFetching, running, stale, tickedCount }) {
  if (running) return 'Applying: wait for the run to finish, or stop it';
  if (!template) return 'This template is gone';
  if (applyVersionOf(template) === null) return "This template's version is not known: reload the list";
  if (stale || showsVersionChanged(template, showsData)) return 'Reload first: the template was re-saved since this list loaded';
  if (isFetching || !showsData) return 'Reading the shows…';
  if (!count(tickedCount)) return 'Tick at least one show';
  return null;
}

const sum = (list, key) => list.reduce((n, s) => n + count(s[key]), 0);

// The ticked shows with other groups: their listings that a template group
// fits move into it, and the old ladders close up
function takesText(withOthers) {
  const groups = sum(withOthers, 'otherGroups');
  const auto = Math.min(sum(withOthers, 'otherAuto'), groups);
  const head = "Listings on those shows' own groups that a template group fits move into it, ranked ones too, "
    + `and the old ladders close up (${plural(groups, 'other group')} on ${plural(withOthers.length, 'show')}; `
    + "Lion Checker groups and other templates' groups keep theirs).";
  if (!auto) return head;
  const tail = auto === 1
    ? '1 of those groups is on Auto: its ranks close up and reprice on its next run.'
    : `${auto} of those groups are on Auto: their ranks close up and reprice on their next run.`;
  return `${head} ${tail}`;
}

// The ticked shows' linked groups on Auto: their filter fields change
function linkedAutoText(n) {
  if (n === 1) {
    return "1 linked group on Auto takes the template's sections, rows, Qty, splits and notes filter where they changed, "
      + 'and reprices on its next run; its floor, ladder and hold stay.';
  }
  return `${n} linked groups on Auto take the template's sections, rows, Qty, splits and notes filter where they changed, `
    + 'and reprice on their next run; their floor, ladder and hold stay.';
}

/**
 * The confirm before an apply, or null when none is needed: asked when a
 * ticked show has groups of its own (otherGroups: their listings that a
 * template group fits move, ranked ones too) or linked groups on Auto
 * (linkedAuto). Paragraphs are split by a blank line (window.confirm text).
 * @param {object[]} shows  the ticked shows (tickedShows)
 * @returns {string|null}
 */
export function applyConfirmText(shows) {
  const list = asArray(shows).filter(isShow);
  const withOthers = list.filter(s => count(s.otherGroups) > 0);
  const linkedAuto = sum(list, 'linkedAuto');
  if (!withOthers.length && !linkedAuto) return null;
  const parts = [`Apply this template to ${plural(list.length, 'show')}?`];
  if (withOthers.length) parts.push(takesText(withOthers));
  if (linkedAuto) parts.push(linkedAutoText(linkedAuto));
  return parts.join('\n\n');
}

const SKIP_TEXT = {
  NO_SECTIONS: 'no section found',
  GROUP_LIMIT: 'no room',
};
const MAX_NAMES = 3;
const MAX_SECTIONS = 6;
const nameOrKey = (x) => trimmed(x?.name) || trimmed(x?.key) || '?';

function skippedText(skipped) {
  if (!skipped.length) return '';
  const named = skipped.slice(0, MAX_NAMES).map(s => `${nameOrKey(s)} (${SKIP_TEXT[s?.reason] || 'skipped'})`);
  const rest = skipped.length - named.length;
  return `${skipped.length} skipped: ${named.join(', ')}${rest > 0 ? ` +${rest} more` : ''}`;
}

function missingText(missing) {
  const names = [...new Set(missing.flatMap(m => asArray(m?.sections).map(s => trimmed(String(s ?? '')) || '?')))];
  if (!names.length) return '';
  return `${plural(names.length, 'section')} not found: ${cutList(names, MAX_SECTIONS)}`;
}

// The linked groups whose key left the template: this apply unlinked them
// (they are regular groups now, never deleted)
function orphansText(orphans) {
  if (!orphans.length) return '';
  const names = cutList(orphans.map(o => nameOrKey(o)), MAX_NAMES);
  return orphans.length === 1
    ? `1 group no longer in the template is now a regular group: ${names}`
    : `${orphans.length} groups no longer in the template are now regular groups: ${names}`;
}

/**
 * An apply that did not cover everything: some template groups skipped,
 * sections not found, or listings whose move did not hold (missed)
 * @param {object|null} result  the POST apply answer
 */
export function isPartialResult(result) {
  return asArray(result?.skipped).length > 0
    || asArray(result?.missingSections).length > 0
    || asArray(result?.missed).length > 0;
}

/**
 * One show's apply answer in words: "5 created · 2 updated · 14 moved (3
 * ranked) · 2 sections not found: 101, 102". Then listings whose move did
 * not hold, skipped groups (and why), and linked groups whose key left the
 * template (unlinked: regular groups now). Nothing created, updated or
 * moved: "Up to date: 3 groups already match", or "Nothing changed".
 * @param {object|null} result  the POST apply answer
 */
export function applyResultText(result) {
  const created = asArray(result?.created).length;
  const updated = asArray(result?.updated).length;
  const moved = asArray(result?.moved);
  const ranked = moved.filter(m => isObject(m) && m.ranked).length;
  const missed = asArray(result?.missed).length;
  const unchanged = asArray(result?.unchanged).length;
  const parts = [];
  if (created) parts.push(`${created} created`);
  if (updated) parts.push(`${updated} updated`);
  if (moved.length) parts.push(`${moved.length} moved${ranked ? ` (${ranked} ranked)` : ''}`);
  if (missed) parts.push(`${plural(missed, 'listing')} stayed in ${missed === 1 ? 'its group' : 'their groups'}`);
  if (!parts.length) {
    parts.push(unchanged
      ? `Up to date: ${plural(unchanged, 'group')} already ${unchanged === 1 ? 'matches' : 'match'}`
      : 'Nothing changed');
  }
  const extras = [
    skippedText(asArray(result?.skipped)),
    missingText(asArray(result?.missingSections)),
    orphansText(asArray(result?.orphans)),
  ];
  return [...parts, ...extras].filter(Boolean).join(' · ');
}

// ---- errors ----

const FALLBACK = 'The template call failed.';
const NO_ANSWER = 'The server did not answer: reload to see whether it went through.';
const READ_NO_ANSWER = 'The server did not answer. Try again.';
const SKYBOX_DOWN = 'Skybox did not answer. Try again in a minute.';
const BY_CODE = {
  NOT_FOUND: 'Not found: the template may have been deleted. Reload the list.',
  ARTIST_SCOPE: 'Only an admin, or a pricer assigned to this artist, can save, update or delete its templates. '
    + 'You can still apply them to shows you price.',
  ARTIST_UNKNOWN: "This show's performer is not known, so it can't have templates.",
  TEMPLATE_WRONG_ARTIST: "That show is another artist's: a template applies only to its own performer's shows.",
};

/**
 * A call whose outcome is unknown (it may have gone through): no answer at
 * all, a 502 or 504 (a proxy's), or any 5xx whose body is not the server's
 * JSON { error } (a 503 the server sends carries { error, retryable }).
 * @param {object|null} err  the axios error
 */
export function isUnknownOutcome(err) {
  if (!err) return false;
  const status = err.response?.status;
  if (!status) return true;
  if (status === 502 || status === 504) return true;
  const data = err.response.data;
  return status >= 500 && !(isObject(data) && typeof data.error === 'string');
}

// TEMPLATE_NO_SECTIONS: no template group would be created, updated or
// unchanged; details lists the section names not found ('#id' for a nameless one)
function noSectionsText(details) {
  const lines = detailLines(details);
  const head = "None of the template's sections are on this show";
  return lines.length ? `${head}: ${cutList(lines, MAX_SECTIONS)}` : `${head}.`;
}

function rejectedText(data) {
  const lines = detailLines(data.details);
  if (data.code === 'TEMPLATE_INVALID') {
    return lines.length ? `The template was not accepted: ${lines.join('; ')}` : 'The template was not accepted.';
  }
  if (data.code === 'AUTO_NEEDS_FLOOR') {
    const head = 'A group on Auto that would lose listings has no floor (group, show or artist)';
    return lines.length ? `${head}: ${lines.join('; ')}` : `${head}. Set a floor on that show first.`;
  }
  if (lines.length) return `Not accepted: ${lines.join('; ')}`;
  return data.error ? `Not accepted: ${data.error}` : 'Not accepted.';
}

/**
 * A failed template call in words, per the contract's codes. read: a GET
 * (no answer means try again; a write with no answer, or any other unknown
 * outcome, may have gone through: reload to check). A 503 the server marks
 * retryable (a Skybox read, the performer read, a cold inventory cache)
 * reads as the server's own message.
 * @param {object|null} err  the axios error
 * @param {{ read?: boolean }} [opts]
 */
export function templateErrorText(err, { read = false } = {}) {
  if (!err) return FALLBACK;
  const status = err.response?.status;
  const raw = err.response?.data;
  const data = isObject(raw) ? raw : {};
  if (isUnknownOutcome(err)) return read ? READ_NO_ANSWER : NO_ANSWER;
  if (status === 429) return `Too many requests. Try again in ${retryAfterSec(err)} s.`;
  if (data.code === 'TEMPLATE_STALE') return templateStaleText(data.version);
  if (data.code === 'TEMPLATE_NO_SECTIONS') return noSectionsText(data.details);
  if (BY_CODE[data.code]) return BY_CODE[data.code];
  if (status === 400) return rejectedText(data);
  if (status === 403) return 'You do not have access to this show.';
  // A bare { error } with no code is the server's unknown-route answer
  if (status === 404) return data.code ? (data.error || 'Not found.') : 'Templates are not available on this server yet.';
  if (status === 503 && data.retryable) return trimmed(data.error) || SKYBOX_DOWN;
  return data.error || FALLBACK;
}

/**
 * A failed call in words with its style: kind 'warn' for a write whose
 * outcome is unknown (reload to check), 'bad' for a refusal or a failed read.
 * @param {object|null} err  the axios error
 * @param {{ read?: boolean }} [opts]
 * @returns {{ text: string, kind: 'warn'|'bad' }}
 */
export function templateErrorInfo(err, { read = false } = {}) {
  return { text: templateErrorText(err, { read }), kind: !read && isUnknownOutcome(err) ? 'warn' : 'bad' };
}

/** A failed read worth a Retry button: not a refusal (400, 403, 404, 409) */
export function templateReadRetryable(err) {
  if (!err) return false;
  return ![400, 403, 404, 409].includes(err.response?.status);
}

/** A 409 TEMPLATE_STALE: the template was re-saved since the list loaded */
export function isTemplateStale(err) {
  return err?.response?.status === 409 && err.response.data?.code === 'TEMPLATE_STALE';
}

// ---- the run ----

// The errors that stop a run (the rest would fail the same way): re-saved
// since (stale), rate limited, or the template gone (a 404)
function stopKindOf(err) {
  if (isTemplateStale(err)) return 'stale';
  const status = err?.response?.status;
  if (status === 429) return 'rate_limited';
  if (status === 404) return 'gone';
  return null;
}

/**
 * Applies the template to each show ONE AT A TIME (await each, never in
 * parallel), in the order given; a show listed twice runs once. Each row
 * goes waiting → applying → done { result } (a clean result) | partial {
 * result } (skipped groups, sections not found or listings that stayed) |
 * failed { error } | unknown { error } (no answer, a 502 / 504: it may have
 * gone through). A failure marks its show and the run goes on, except a 409
 * TEMPLATE_STALE, a 429 or a 404 (the template deleted), which stop it:
 * nothing more is sent and the rest read stopped { reason }.
 * shouldContinue is asked before each show ("Stop after this show", the
 * modal closed): false stops the run there, the show in flight finishing.
 * @param {object[]} shows  the ticked shows
 * @param {(show) => Promise<object>} applyOne  one show's apply (the mutation)
 * @param {{ onUpdate?: (key, rowState) => void, shouldContinue?: () => boolean }} [opts]
 * @returns {Promise<{ results: object, stop: { kind: 'stale'|'rate_limited'|'gone'|'stopped', message: string }|null }>}
 */
export async function runTemplateApply(shows, applyOne, { onUpdate, shouldContinue } = {}) {
  const seen = new Set();
  const list = asArray(shows).filter((s) => {
    if (!isShow(s) || seen.has(showKey(s))) return false;
    seen.add(showKey(s));
    return true;
  });
  const results = {};
  const put = (key, state) => {
    results[key] = state;
    onUpdate?.(key, state);
  };
  const stopFrom = (i, reason, stop) => {
    for (const rest of list.slice(i)) put(showKey(rest), { status: 'stopped', reason });
    return { results, stop };
  };
  for (const s of list) put(showKey(s), { status: 'waiting' });
  for (let i = 0; i < list.length; i++) {
    if (shouldContinue && !shouldContinue()) {
      return stopFrom(i, 'stopped', { kind: 'stopped', message: 'Stopped: the shows after it were not applied.' });
    }
    const key = showKey(list[i]);
    put(key, { status: 'applying' });
    try {
      const result = await applyOne(list[i]);
      put(key, { status: isPartialResult(result) ? 'partial' : 'done', result });
    } catch (err) {
      const error = templateErrorText(err);
      if (isUnknownOutcome(err)) {
        put(key, { status: 'unknown', error });
        continue;
      }
      put(key, { status: 'failed', error });
      const kind = stopKindOf(err);
      if (kind) return stopFrom(i + 1, kind, { kind, message: error });
    }
  }
  return { results, stop: null };
}

/** The ticks after a run: the shows applied cleanly untick; partial, failed, unknown and stopped ones stay */
export function untickDone(selected, results) {
  const next = new Set(asSet(selected));
  for (const [key, state] of Object.entries(isObject(results) ? results : {})) {
    if (state?.status === 'done') next.delete(key);
  }
  return next;
}

const STOPPED_TEXT = {
  stale: 'Not applied: reload first',
  stopped: 'Not applied: stopped',
};

/**
 * A row's run state in words: { text, kind: 'muted'|'ok'|'warn'|'bad' }, or
 * null when the show was not in a run. Partial and unknown outcomes read in
 * warn style.
 */
export function rowStateText(state) {
  if (!isObject(state)) return null;
  switch (state.status) {
    case 'waiting': return { text: 'Waiting', kind: 'muted' };
    case 'applying': return { text: 'Applying…', kind: 'muted' };
    case 'done': return { text: applyResultText(state.result), kind: 'ok' };
    case 'partial': return { text: applyResultText(state.result), kind: 'warn' };
    case 'failed': return { text: state.error || FALLBACK, kind: 'bad' };
    case 'unknown': return { text: state.error || NO_ANSWER, kind: 'warn' };
    case 'stopped': return { text: STOPPED_TEXT[state.reason] || 'Not applied', kind: 'muted' };
    default: return null;
  }
}

/**
 * The footer's run line: "Applying 2 of 3…" while it runs, then "Applied to
 * 2 shows · 1 partial · 1 outcome unknown · 1 failed · 1 not applied"
 * (applied counts the clean and the partial ones)
 */
export function applyRunText(results) {
  const states = Object.values(isObject(results) ? results : {}).filter(isObject);
  if (!states.length) return '';
  const by = (status) => states.filter(s => s.status === status).length;
  const waiting = by('waiting');
  if (waiting + by('applying')) return `Applying ${states.length - waiting} of ${states.length}…`;
  const parts = [`Applied to ${plural(by('done') + by('partial'), 'show')}`];
  if (by('partial')) parts.push(`${by('partial')} partial`);
  if (by('unknown')) parts.push(`${by('unknown')} outcome unknown`);
  if (by('failed')) parts.push(`${by('failed')} failed`);
  if (by('stopped')) parts.push(`${by('stopped')} not applied`);
  return parts.join(' · ');
}

// ---- the list ----

/** "by Albert · 2h ago" (the last save); either part left out when missing */
export function templateStampText(t) {
  if (!isObject(t)) return '';
  const by = trimmed(t.updatedByName) ? `by ${trimmed(t.updatedByName)}` : '';
  const when = parseUtcDate(t.updatedAt) ? agoText(t.updatedAt) : '';
  return [by, when].filter(Boolean).join(' · ');
}

/** The Groups cell's title: one line per template group, "Orchestra: Orch Center, Orch Left" */
export function templateGroupsTitle(t) {
  return asArray(t?.groups).filter(isObject).map((g) => {
    const sections = asArray(g.sections).map(s => String(s ?? '')).filter(Boolean);
    return `${nameOrKey(g)}: ${sections.length ? sections.join(', ') : 'no section'}`;
  }).join('\n');
}

const quoted = (name) => `“${trimmed(name) || 'template'}”`;

/**
 * A save's or update's answer in words: "Saved “Wicked”: 3 groups linked."
 * / "Updated “Wicked” to version 4: 2 groups linked · 1 unlinked (kept as a
 * hand-made group). Apply it to …"
 * @param {object|null} data  { template, linked, unlinked? }
 * @param {'save'|'update'} [mode]
 */
export function saveResultText(data, mode = 'save') {
  if (!isObject(data)) return 'Saved.';
  const name = quoted(data.template?.name);
  const linked = plural(asArray(data.linked).length, 'group');
  if (mode !== 'update') return `Saved ${name}: ${linked} linked.`;
  const unlinked = asArray(data.unlinked).length;
  const kept = unlinked
    ? ` · ${unlinked} unlinked (kept as ${unlinked === 1 ? 'a hand-made group' : 'hand-made groups'})`
    : '';
  const version = versionOf(data.template?.version);
  return `Updated ${name}${version ? ` to version ${version}` : ''}: ${linked} linked${kept}. `
    + 'Apply it to the other dates to bring them up to date.';
}

/** The confirm before a delete: the linked groups stay as hand-made groups */
export function deleteConfirmText(t) {
  return `Delete the template ${quoted(t?.name)}? The groups it made stay on their shows as hand-made groups: `
    + 'nothing on any show is deleted or repriced.';
}

/** A delete's answer { id, unlinked } in words */
export function deleteResultText(data, name) {
  if (!isObject(data)) return 'Deleted.';
  const n = count(data.unlinked);
  return n ? `Deleted ${quoted(name)}: ${plural(n, 'group')} unlinked (kept).` : `Deleted ${quoted(name)}.`;
}
